import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MicrophoneIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { apiService } from '@/services/api';

interface QuickDictationRecorderProps {
  isOpen: boolean;
  onClose: () => void;
  onTranscriptionComplete: (transcription: string) => void;
  sectionLabel: string;
  appointmentId: string;
  sectionKey: string;
}

/**
 * Quick Dictation Recorder
 *
 * Simplified audio recorder for physician notes (5-60 seconds).
 * NO encryption, NO queuing - just fast transcription for clinical summaries.
 *
 * Features:
 * - Simple start/stop interface
 * - Real-time waveform visualization
 * - Audio level indicator
 * - Auto-uploads on stop
 * - Returns transcription to parent
 */
export const QuickDictationRecorder: React.FC<QuickDictationRecorderProps> = ({
  isOpen,
  onClose,
  onTranscriptionComplete,
  sectionLabel,
  appointmentId,
  sectionKey
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const MAX_RECORDING_TIME = 60; // 60 seconds max

  // Audio level monitoring
  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average volume
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    const level = Math.min(100, (average / 255) * 100);

    setAudioLevel(level);

    // Update waveform data (keep last 50 samples)
    setWaveformData(prev => {
      const newData = [...prev, level];
      return newData.slice(-50);
    });

    animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000 // Optimized for speech transcription
        }
      });

      streamRef.current = stream;
      chunksRef.current = [];

      // Setup audio context for visualization
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start audio level monitoring
      monitorAudioLevel();

      // Create MediaRecorder
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Calculate actual duration from start time (avoid race condition with state)
        const actualDuration = Math.floor((Date.now() - startTimeRef.current) / 1000);

        // Validate duration (minimum 1 second)
        const validDuration = Math.max(1, actualDuration);

        console.log('Recording stopped:', {
          actualDuration,
          validDuration,
          startTime: startTimeRef.current
        });

        // Create audio blob
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });

        // Upload and transcribe with validated duration
        await uploadAndTranscribe(audioBlob, validDuration);
      };

      mediaRecorder.start(1000); // Collect data every second
      startTimeRef.current = Date.now();
      setIsRecording(true);

      // Update recording time
      intervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingTime(elapsed);

        // Auto-stop at max time
        if (elapsed >= MAX_RECORDING_TIME) {
          stopRecording();
          toast('Maximum recording time reached', { icon: '⏱️' });
        }
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  }, [recordingTime]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsRecording(false);
  }, []);

  // Upload and transcribe
  const uploadAndTranscribe = async (audioBlob: Blob, duration: number) => {
    setIsUploading(true);

    try {
      // Convert blob to base64 for API upload
      const reader = new FileReader();

      reader.onloadend = async () => {
        try {
          const base64Audio = reader.result as string;

          // Call API endpoint to transcribe
          const response = await apiService.post<{
            success: boolean;
            data: { transcription: string };
          }>(
            `/v1/clinical/${appointmentId}/summary/${sectionKey}/dictate`,
            {
              audioData: base64Audio,
              duration: duration
            }
          );

          if (response.success && response.data.transcription) {
            toast.success('Transcription complete');
            onTranscriptionComplete(response.data.transcription);
            handleClose();
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error) {
          console.error('Failed to transcribe:', error);
          // The axios interceptor already toasts user-facing messages for any
          // HTTP error response (both the 422 "no speech detected" path and
          // generic 5xx "Server error"). Only show a fallback toast for errors
          // that never reached the server (network failure, invalid response shape).
          const wasToastedByInterceptor = isAxiosError(error) && !!error.response;
          if (!wasToastedByInterceptor) {
            toast.error('Failed to transcribe audio');
          }
          setIsUploading(false);
        }
      };

      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Failed to process audio:', error);
      toast.error('Failed to process audio');
      setIsUploading(false);
    }
  };

  // Format time display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle close
  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    setRecordingTime(0);
    setAudioLevel(0);
    setWaveformData([]);
    setIsUploading(false);
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isRecording) {
        stopRecording();
      }
    };
  }, [isRecording, stopRecording]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Quick Dictation
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {sectionLabel}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isUploading}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Recording Status */}
              <div className="mb-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="text-4xl font-mono font-semibold text-gray-900">
                    {formatTime(recordingTime)}
                  </div>
                  <div className="ml-3 text-sm text-gray-500">
                    / {MAX_RECORDING_TIME}s
                  </div>
                </div>

                {/* Waveform Visualization */}
                <div className="h-20 bg-gray-50 rounded-lg flex items-center justify-center gap-1 px-4">
                  {waveformData.length > 0 ? (
                    waveformData.map((level, index) => (
                      <div
                        key={index}
                        className="flex-1 bg-blue-500 rounded-full transition-all duration-100"
                        style={{
                          height: `${Math.max(4, level)}%`,
                          opacity: 0.3 + (level / 100) * 0.7
                        }}
                      />
                    ))
                  ) : (
                    <div className="text-gray-400 text-sm">
                      {isRecording ? 'Recording...' : 'Ready to record'}
                    </div>
                  )}
                </div>

                {/* Audio Level Indicator */}
                {isRecording && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Audio Level</span>
                      <span>{Math.round(audioLevel)}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-green-400 to-blue-500"
                        animate={{ width: `${audioLevel}%` }}
                        transition={{ duration: 0.1 }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex gap-3">
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    disabled={isUploading}
                    className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <MicrophoneIcon className="w-5 h-5" />
                    Start Recording
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    disabled={isUploading}
                    className="flex-1 py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <StopIcon className="w-5 h-5" />
                    {isUploading ? 'Transcribing...' : 'Stop & Transcribe'}
                  </button>
                )}
              </div>

              {/* Helper Text */}
              <p className="text-xs text-gray-500 text-center mt-4">
                {isRecording
                  ? 'Click "Stop & Transcribe" when finished'
                  : `Record a quick note (max ${MAX_RECORDING_TIME} seconds)`
                }
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
